import React, { useCallback, useEffect, useRef, } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { closeModal } from '../../../../core/actions';
import { getTabbableElements } from '../../../../core/helpers/get-tabbable-elements';
import { BackgroundEffectsPanel } from '..';
import './styles.scss';
export function BackgroundEffectsModal() {
    const intl = useIntl();
    const dispatch = useDispatch();
    const containerRef = useRef();
    const close = useCallback(() => dispatch(closeModal()), [dispatch]);
    useEffect(() => {
        const container = containerRef.current;
        const previouslyFocused = document.activeElement;
        const tabbable = container ? getTabbableElements(container) : [];
        if (tabbable.length > 0) {
            tabbable[0].focus();
        }
        else {
            container?.focus();
        }
        function handleClick(e) {
            if (!containerRef.current.contains(e.target)) {
                /**
                 * Don't close the modal if the outside click was any of the following:
                 */
                const ALLOWED_IDS = ['backgroundEffects.options.branded.disabled'];
                if (e.target instanceof Element && ALLOWED_IDS.includes(e.target.id)) {
                    return;
                }
                close();
            }
        }
        document.addEventListener('mousedown', handleClick);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.body.style.overflow = 'visible';
            if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
                previouslyFocused.focus();
            }
        };
    }, [close]);
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            close();
            return;
        }
        if (event.key !== 'Tab') {
            return;
        }
        const container = containerRef.current;
        if (!container) {
            return;
        }
        const tabbable = getTabbableElements(container);
        if (tabbable.length === 0) {
            event.preventDefault();
            return;
        }
        const first = tabbable[0];
        const last = tabbable[tabbable.length - 1];
        const active = document.activeElement;
        if (!active || !container.contains(active)) {
            event.preventDefault();
            first.focus();
            return;
        }
        if (active === container) {
            event.preventDefault();
            if (event.shiftKey) {
                last.focus();
            }
            else {
                first.focus();
            }
            return;
        }
        if (event.shiftKey) {
            if (active === first) {
                event.preventDefault();
                last.focus();
            }
        }
        else if (active === last) {
            event.preventDefault();
            first.focus();
        }
    }, [close]);
    return createPortal(<div ref={containerRef} className="background-effects__modal" role="dialog" aria-modal="true" aria-labelledby="background-effects__modal__title" tabIndex={-1} onKeyDown={handleKeyDown}>
      <header>
        <h2 id="background-effects__modal__title" className="background-effects__modal__title">
          <FormattedMessage id="backgroundEffects.title"/>
        </h2>
        <button className="background-effects__modal__close-button" aria-label={intl.formatMessage({ id: 'common.close' })} onClick={close}>
          <i className="icon-close"/>
        </button>
      </header>
      <BackgroundEffectsPanel />
    </div>, document.body);
}
